
import { useEffect, useRef, useState } from 'react';

import { showToast } from '@turingshequ/react-toast/src'

import { reasonList, CONVENTION, AGREEMENT } from '../constants';
import service, {uploadImgBase64, API} from 'fetch/service';
import { escapeHTML } from 'util/index';
import { YDLog } from 'components/YDLog'
import {insertHtmlAtCaret, judgeFocusNodeIs} from './utils';
import s from './index.module.scss';

export enum REPORT_TYPE {
  work = 1,
  comment = 2,
  reply = 3,
  user = 4
}

const TITLE = {
  [REPORT_TYPE.work]: '作品',
  [REPORT_TYPE.comment]: '评论',
  [REPORT_TYPE.reply]: '回复',
  [REPORT_TYPE.user]: '作者'
}

const FETCH_URL = {
  [REPORT_TYPE.work]: API.works.report,
  [REPORT_TYPE.comment]: API.works.reportComment,
  [REPORT_TYPE.reply]: API.works.reportReply,
  [REPORT_TYPE.user]: API.works.reportUser
}

const FETCH_ID_KEY = {
  [REPORT_TYPE.work]: 'worksIdStr',
  [REPORT_TYPE.comment]: 'commentId',
  [REPORT_TYPE.reply]: 'replyId',
  [REPORT_TYPE.user]: 'userId'
}



interface Props {
    onClose: (type?: string) => void;
    id: string
    type: REPORT_TYPE
}

/**
 * @description 举报作品的弹窗
 * @returns
 */

const ToReport:React.FC<Props> = ({
    onClose,
    id,
    type,
}) => {

    const [loading, setLoading] = useState(false);
    const [reasonType, setReasonType] = useState(0);
    const [fontNum, setFontNum] = useState(0);
    const [copyLink, setCopyLink] = useState('');
    const textareaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.addEventListener('paste', handlePaste)
        return () => {
            document.removeEventListener('paste', handlePaste)
        }
    }, [])

    const fontNumberWarning = fontNum > 600;

    // 只有作品举报是需要第一个选项(抄袭作品)的
    const reasonListData = type === REPORT_TYPE.work
      ? reasonList
      : reasonList.slice(1);

    return (<div className={s.wrap}>
        <div className={s.title}>{TITLE[type]}举报</div>
        <div className={s.reason}>
            <div className={s.title2}>举报原因</div>
            <div className={s.reasonList}>
                {reasonListData.map(item =>
                    (<span
                      key={item.key}
                        className={`${s.reasonItem} ${item.key === reasonType ? s.activeItem : ''}`}
                        onClick={() => setReasonType(item.key)}
                      >
                        {item.text}
                    </span>)
                )}
            </div>
        </div>
        <div className={s.reason}>
            {
              reasonType === reasonList[0]?.key && (
                <>
                  <div className={s.title2}>被抄袭作品链接:</div>
                  <div>
                    <input
                      className={`${s.copyLinkInput}`}
                      placeholder="写清楚你当前举报的作品是抄袭哪个作品，便于管理员核查～ "
                      value={copyLink}
                      onChange={(e) => setCopyLink(e.target.value)}
                    />
                  </div>
                </>
              )
            }
            <div className={s.title2}>
                具体线索
                <span className={s.descText}>{
                  `* 请搜集好所有证据再举报哦！每人每个${TITLE[type]}仅有一次举报机会～`
                }</span>
            </div>
            <div className={s.textareaWrap} onClick = {() => { textareaRef?.current?.focus() }}>
                <div
                    id='textarea'
                    ref={textareaRef}
                    className={`${s.textarea} textarea`}
                    contentEditable="true"
                    data-placeholder="请详细描述举报原因，可直接粘贴图片，图文并茂更有助于举报成功哦~"
                    onKeyUpCapture={contentChanged}
                >
                </div>
                <span className={`${s.fontNumber} ${fontNumberWarning && s.fontNumErr}`}>{fontNum}/600</span>
            </div>
        </div>
        <div className={s.desc}>
            有道小图灵
            <a target='_blank' href={CONVENTION} rel="noreferrer">《社区公约》</a>
            和 <a href={AGREEMENT} target='_blank' rel="noreferrer">《服务协议》</a>
        </div>
        <div className={s.handleWrap}>
          <YDLog
            click={{
              name: 'click',
              data: {
                button_name: '举报',
                jubao_type: `举报${TITLE[type]}`,
              }
            }}
          >
            <button
                className={`${s.btn} ${s.btnSure} ${fontNumberWarning ? s.notAllow : ''}`}
                onClick={handleSure}
            >
                举报
            </button>
            </YDLog>
            <button
                className={`${s.btn} ${s.btnCancel}`}
                onClick={() => onClose()}
            >
                取消
            </button>
        </div>
    </div>)

    function handleSure() {

        if(fontNumberWarning) {
            return;
        }
        if(reasonType === 0) {
            showToast({
                type: 'error',
                message: '请选择举报原因～'
            })
            return;
        }

        if(reasonType === reasonList[0].key && !copyLink) {
          showToast({
              type: 'error',
              message: '请填写抄袭作品链接～'
          })
          return;
        }

        if(reasonType === reasonList[0].key && copyLink) {
          // 正则判断是合法的url
          const reg = /^(https|http):\/\/[^\s]+$/;
          const isLegal = reg.test(copyLink);
          if(!isLegal) {
            showToast({
              type: 'error',
              message: '请填写合法的抄袭作品链接～'
            })
            return;
          }
        }

        if(!textareaRef.current?.innerHTML) {
            showToast({
                type: 'error',
                message: '请输入具体线索～'
            })
            return;
        }
        const reasonString = escapeHTML(textareaRef.current?.innerHTML || '');

        if(loading) {
            return;
        }
        setLoading(true);

        const idKey = FETCH_ID_KEY[type];

        const params = {
            [idKey]: id,
            category: reasonType,
            description: reasonString
        }

        //被抄袭的作品链接（category=1时，该字段不为空）
        if(type === REPORT_TYPE.work) {
          params['copyWorksUrl'] = escapeHTML(copyLink);
        }

        service.post(FETCH_URL[type], params).then((res: any) => {
            setLoading(false);
            if(res.code !== 0) {
              showToast({
                type: 'default',
                message: res.description
              })
            }else {
              showToast({
                type: 'default',
                message: '举报成功～'
              })
            }
            onClose('success');
        }).catch(() => {
            setLoading(false);
        })
    }

    async function handlePaste(event: ClipboardEvent) {
        if(!judgeFocusNodeIs('textarea')) {
          return;
        }
        event.preventDefault();
        const clp = event.clipboardData;
        const items = event.clipboardData ? event.clipboardData.items : [];
        const file = Array.from(items)
            ?.find(item => item.type.indexOf('image') !== -1)
            ?.getAsFile();
        if(!file) {
            // 去除复制文本的样式标签
            const text = clp?.getData('text/plain') || "";
            if (text !== "") {
                document.execCommand('insertText', false, text);
            }
            return;
        }

        if(file.size > 1024*1024*5) {
            showToast({
                type: 'error',
                message: '请上传5M以内图片哦～'
            })
            return;
        }

        const childNodes = textareaRef.current?.childNodes || [];
        const imgNum = Array.from(childNodes)
            .filter(item => {
                return item?.nodeName === 'IMG' ||
                // 出现换行之后 添加的图片会是 <div><img /><div>
                Array.from(item?.childNodes)?.find(item => item?.nodeName === 'IMG')
            }).length
        if(imgNum >= 3) {
            event.preventDefault();
            showToast({
                type: 'error',
                message: '图片最多三张'
            })
            return;
        }

        try {
            const imgURL = await uploadImgBase64(file);
            const imgHtml = `<img src=${imgURL}>`;
            insertHtmlAtCaret(imgHtml, 'textarea');
        } catch (error) {
            showToast({
                type: 'error',
                message: '图片插入失败 请稍后再试'
            })
        }

    }

    function contentChanged() {
        const strDelImage = textareaRef.current?.textContent || '';
        const number = strDelImage.length;
        setFontNum(number);
    }
}

export default ToReport;
